/**
 * StudentPortal's single point for persisting an authenticated session.
 *
 * Mirror of Sandbox's `Core/utils/auth.js` → `saveSession`, adapted to the
 * portal's `localStorage.user` shape. Every login / profile-switch / token
 * refresh path funnels through here so that the portal's localStorage, the
 * `cy-user` checkout key, the cached profile pic, and — crucially — the shared
 * cross-app `Authorization` / `refresh-token` cookies on `.codeyoung.com` never
 * drift apart.
 *
 * The shared cookies are how the **standalone Sandbox** (and a fresh Portal
 * tab) auto-logs-in: see `bootstrapSessionFromCookie.js`. Writing them here —
 * plus bumping the `cy_auth_sync` flag — means a Portal profile switch
 * propagates to an already-open Sandbox tab (it re-syncs on focus via
 * `authSyncListener.js`).
 */

import { jwtDecode } from "jwt-decode";
import * as Sentry from "@sentry/react";
import { decrypt, encrypt } from "./other";
import {
  setCookie,
  deleteCookieAllDomains,
  clearLoggedOutFlag,
  getSharedCookieDomain,
} from "./cookieUtils";

const ONE_YEAR_SECONDS = 365 * 24 * 60 * 60;

const writeAuthCookies = (accessToken, refreshToken) => {
  const domain = getSharedCookieDomain();
  // Clear any stale same-named cookies on OTHER domains first. A cookie written
  // by an older build on a different domain (e.g. `.codeyoung.com` vs
  // `.dev.codeyoung.com`) would otherwise linger and could win in `getCookie`
  // (cross-domain duplicates order unpredictably in document.cookie). After this
  // there is exactly one auth cookie, on the canonical shared domain.
  deleteCookieAllDomains("Authorization");
  deleteCookieAllDomains("refresh-token");
  setCookie("Authorization", accessToken, {
    domain,
    maxAge: ONE_YEAR_SECONDS,
    sameSite: "Lax",
  });
  setCookie("refresh-token", refreshToken, {
    domain,
    maxAge: ONE_YEAR_SECONDS,
    sameSite: "Lax",
  });
  // Sync flag: bumped on every session write so other tabs / the other app can
  // detect "something changed, re-read the shared cookies" (e.g. profile switch).
  setCookie("cy_auth_sync", String(Date.now()), {
    domain,
    maxAge: ONE_YEAR_SECONDS,
    sameSite: "Lax",
  });
};

/**
 * Persists auth tokens + user profile and syncs the shared cookies.
 *
 * @param {Object} params
 * @param {string} params.accessToken    cy-sso JWT access token
 * @param {string} params.refreshToken   cy-sso refresh token
 * @param {Object} [params.userData={}]  Full user object from the auth/profile API
 *   (studentName, studentId, studentEmail, profilePicUrl, role, …).
 */
export const saveSession = ({ accessToken, refreshToken, userData = {} }) => {
  let decoded = {};
  try {
    decoded = jwtDecode(accessToken) || {};
  } catch (_) {
    decoded = {};
  }

  // Keep this object shape identical to Sandbox's saveSession (Core/utils/auth.js)
  // so both apps store the exact same session structure under the exact same
  // localStorage keys — required for cross-app compatibility (a session written
  // by one app must be fully readable by the other).
  const sessionData = {
    ACCESS_TOKEN: accessToken,
    REFRESH_TOKEN: refreshToken,
    Authorization: accessToken,
    "refresh-token": refreshToken,
    role: userData.role || decoded.role || "student",
    user_id: userData.userId || decoded.userId || "",
    email: userData.email || decoded.studentEmail || decoded.email || "",
    studentEmail: userData.email || decoded.studentEmail || decoded.email || "",
    userName: userData.userName || "",
    studentName: userData.studentName || decoded.studentName || "",
    profilePicUrl: userData.profilePicUrl || "",
    isPaid: userData.isPaid ?? false,
    studentId: userData.userId || decoded.userId || "",
    userId: userData.userId || decoded.userId || "",
    grade: userData.grade || decoded.grade || "",
  };

  const encrypted = encrypt(sessionData);
  // Same keys Sandbox's saveSession writes, for cross-app compatibility…
  localStorage.setItem("sandbox_user", encrypted);
  localStorage.setItem("studentId", sessionData.studentId); // legacy key for MFE
  localStorage.setItem("sandbox_user_data", encrypted);
  localStorage.setItem("user", encrypted);

  // Identify the user on Sentry so auth-flow events stop being anonymous.
  // Id only — no email/name, keeping PII out of the FE project.
  if (sessionData.userId) {
    Sentry.setUser({ id: sessionData.userId });
  }

  // cy-user — plain JSON consumed by the BuyCredits / checkout MFE (portal-only).
  if (accessToken && refreshToken) {
    try {
      localStorage.setItem(
        "cy-user",
        JSON.stringify({
          ACCESS_TOKEN: accessToken,
          REFRESH_TOKEN: refreshToken,
        })
      );
    } catch (_) {
      /* ignore */
    }
  }

  // profile_pic — SP caches the avatar separately (encrypted).
  if (sessionData.profilePicUrl) {
    try {
      localStorage.setItem("profile_pic", encrypt(sessionData.profilePicUrl));
    } catch (_) {
      /* ignore */
    }
  }

  // A fresh session write revives auth — clear any cross-app logout signal so an
  // immediately-following bootstrap/focus check doesn't tear this session down.
  try {
    clearLoggedOutFlag();
  } catch (_) {
    /* no-op */
  }

  // Shared cross-app cookies — the auto-login + cross-tab-sync transport.
  // STUDENT-ONLY (symmetric with Sandbox): only a student session participates
  // in cross-app auto-login. A parent / staff session stays app-local — we don't
  // publish its tokens to Sandbox (and correspondingly don't raise the
  // `cy_logged_out` signal for it). Portal's own tabs still share localStorage.
  try {
    // Impersonated-student sessions (staff viewing as a student; 15m, never
    // refreshable) are excluded — publishing them would plant soon-dead tokens
    // as the browser-wide student session.
    if (sessionData.role === "student" && !decoded.impersonatedBy) {
      writeAuthCookies(accessToken, refreshToken);
    }
  } catch (_) {
    // Cookie failures must never block auth completion.
  }

  // Let live listeners pick up the new tokens without a reload. Dispatch both
  // event names so portal listeners (user_update) and any embedded-Sandbox
  // listeners (sandbox_user_update) react.
  window.dispatchEvent(new Event("user_update"));
  window.dispatchEvent(new Event("sandbox_user_update"));
};

/**
 * Convenience: reads the current portal session, returns the decrypted object
 * or {} — used by callers that need to merge fresh tokens onto existing fields.
 */
export const getStoredUser = () => {
  try {
    return decrypt(localStorage.getItem("user")) || {};
  } catch {
    return {};
  }
};

export default saveSession;
